import { Slider } from '../ui/Slider';
import { WizardStepHeader } from '../wizard';
import pt from '../../locales/pt';
import {
  MAX_MONTHS,
  MIN_MONTHS,
  MONTH_MARKS,
  clampMonths,
  composeFundSplit,
  computeTotalTarget,
  getActiveMonthMark,
  getMonthsMilestone,
  parseAmount,
} from '../../lib/emergencyFundDisplayUtils';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';

export function EmergencyFundMonthsStep({ months, onMonthsChange, avgMonthly }) {
  const copy = pt.emergencyFund.wizard.months;
  const safeMonths = clampMonths(months);
  const totalTarget = computeTotalTarget(avgMonthly, safeMonths);
  const milestone = getMonthsMilestone(safeMonths, copy);
  const activeMark = getActiveMonthMark(safeMonths);
  const spend = parseAmount(avgMonthly);
  const split = composeFundSplit(avgMonthly, safeMonths);

  return (
    <div className="flex flex-col gap-[18px]">
      <WizardStepHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <div className="rounded-[20px] bg-gradient-to-br from-sage-600 to-sage-800 px-[18px] py-5 text-center shadow-[0_16px_30px_-16px_rgba(74,98,67,0.7)]">
        <p className="text-xs font-semibold text-sage-100/90">{milestone.title}</p>
        <p className="mt-1 font-display text-[38px] font-bold leading-none tracking-tight text-white tabular-nums">
          {totalTarget > 0 ? formatEUR(totalTarget) : '—'}
        </p>
        <p className="mt-1 text-[12.5px] text-sage-100/80">
          {copy.breakdown
            .replace('{months}', String(safeMonths))
            .replace('{spend}', spend > 0 ? formatEUR(spend).replace(/\s*€$/, '').trim() : '—')}
        </p>
      </div>

      <div className="px-1">
        <Slider
          value={[safeMonths]}
          min={MIN_MONTHS}
          max={MAX_MONTHS}
          step={1}
          onValueChange={([value]) => onMonthsChange(clampMonths(value))}
          aria-label={copy.title}
        />
        <div className="mt-3 flex justify-between">
          {MONTH_MARKS.map((mark) => {
            const active = mark.at === activeMark;
            const label = copy.marks[mark.key];
            return (
              <button
                key={mark.at}
                type="button"
                onClick={() => onMonthsChange(mark.at)}
                className="flex-1 text-center"
              >
                <div
                  className={cn(
                    'font-display text-sm font-bold',
                    active ? 'text-sage-700 dark:text-sage-300' : 'text-muted-foreground/50',
                  )}
                >
                  {label.num}
                </div>
                <div
                  className={cn(
                    'mt-0.5 text-[9.5px] font-semibold leading-tight',
                    active ? 'text-sage-600 dark:text-sage-400' : 'text-muted-foreground/50',
                  )}
                >
                  {label.name}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {split.totalEur > 0 ? (
        <div className="rounded-[15px] border border-[#F0EFED] bg-[#FAFAF9] px-[15px] py-[13px] dark:border-border dark:bg-muted/40">
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
            {copy.split.title}
          </p>
          <div className="mt-2.5 flex gap-2.5 items-start">
            <span
              className="mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px] bg-sage-500"
              aria-hidden
            />
            <p className="m-0 text-[12.5px] leading-snug text-foreground/90">
              <span className="font-semibold">
                {copy.split.immediateTitle.replace('{amount}', formatEUR(split.immediateEur))}
              </span>
              {': '}
              {copy.split.immediateBody}
            </p>
          </div>
          {split.growingEur > 0 ? (
            <div className="mt-2 flex gap-2.5 items-start">
              <span
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px] bg-sage-800"
                aria-hidden
              />
              <p className="m-0 text-[12.5px] leading-snug text-foreground/90">
                <span className="font-semibold">
                  {copy.split.growingTitle.replace('{amount}', formatEUR(split.growingEur))}
                </span>
                {': '}
                {copy.split.growingBody}
              </p>
            </div>
          ) : null}
          <p className="mt-2.5 mb-0 text-[11.5px] leading-snug text-muted-foreground">
            {copy.split.footer}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default EmergencyFundMonthsStep;
